import {
    IIndicatorResponse,
    MetricTypeCodes,
} from '@kcftech/prometheustypescriptapiclient';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { MaxIndicatorSelections } from 'pages/workbench/side_panel/shared';
import { IndicatorInfo } from 'pages/workbench/types';
import { useMemo } from 'react';
import { toMap } from 'utils/array';
import { queryClient, resultsOf } from 'utils/query';
import { buildOrderBy, Filter } from 'utils/query_v3';

const queryKey = 'trend-indicators';

export function invalidateTrendIndicators() {
    queryClient.invalidateQueries({
        queryKey: [queryKey],
    });
}

export const indicatorDefaultSort: DataSourceSort[] = [
    {
        field: 'SystemSortOrder ',
        dir: 'asc',
    },
    {
        field: 'Id',
        dir: 'asc',
    },
];

export type UserTrendIndicatorsOptions = Readonly<{
    indicatorInfo: IndicatorInfo[];
    groupIds: string[];
    customFilterIds?: string[];
    indicatorTypes?: MetricTypeCodes[];
    sensorRoleTypes?: string[];
}>;

export type TrendIndicator = Readonly<{
    indicatorId: string;
    indicator: IIndicatorResponse;
    color: string;
}>;

export function useTrendIndicators({
    indicatorInfo,
    groupIds,
    customFilterIds,
    indicatorTypes,
    sensorRoleTypes,
}: UserTrendIndicatorsOptions) {
    const sortExpr = buildOrderBy(indicatorDefaultSort);
    const colorLookup = useMemo(
        () =>
            toMap(
                indicatorInfo,
                (info) => info.indicatorId,
                (info) => info.color
            ),
        [indicatorInfo]
    );

    const indicatorIds = useMemo(() => indicatorInfo.map((info) => info.indicatorId), [
        indicatorInfo,
    ]);

    const filter = useMemo(
        () =>
            Filter.and(
                sensorRoleTypes && Filter.in('MeasureOf', sensorRoleTypes),
                Filter.in('Id', indicatorIds)
            ),
        [indicatorIds, sensorRoleTypes]
    );

    return useQuery({
        queryKey: [queryKey, groupIds, filter],

        queryFn: async () => {
            if (indicatorIds.length === 0) {
                return [];
            }
            const indicators = await resultsOf(
                sdClient.getIndicators({
                    groupIds,
                    filterIds: customFilterIds,
                    indicatorTypeIds: indicatorTypes,
                    filter,
                    page: 1,
                    pageLimit: MaxIndicatorSelections,
                    orderBy: sortExpr,
                })
            );

            return indicators.map<TrendIndicator>((indicator) => {
                const color = colorLookup.get(indicator.Id);
                if (!color) {
                    throw new Error('Missing color');
                }

                return {
                    indicatorId: indicator.Id,
                    indicator,
                    color,
                };
            });
        },

        placeholderData: keepPreviousData,
    });
}
